/**
 * Simple event bus for decoupled communication between game systems.
 */
class EventBus {
  constructor() {
    this.listeners = new Map();
  }

  on(event, callback) {
    if (!this.listeners.has(event)) this.listeners.set(event, new Set());
    this.listeners.get(event).add(callback);
    return () => this.off(event, callback);
  }

  off(event, callback) {
    if (this.listeners.has(event)) {
      this.listeners.get(event).delete(callback);
    }
  }

  emit(event, data) {
    if (this.listeners.has(event)) {
      for (const cb of this.listeners.get(event)) {
        try { cb(data); } catch (e) { console.error(`EventBus error [${event}]:`, e); }
      }
    }
  }

  once(event, callback) {
    const unsub = this.on(event, (data) => {
      unsub();
      callback(data);
    });
    return unsub;
  }
}

export const events = new EventBus();

// Event names
export const EVT = {
  PLANET_ENTER:      'planet:enter',
  SHIP_ENTER:        'ship:enter',
  SHIP_EXIT:         'ship:exit',
  SHIP_LAUNCH:       'ship:launch',
  SHIP_LAND:         'ship:land',
  CREATURE_CALL:     'creature:call',
  FLOCK_FORMED:      'flock:formed',
  WARP_START:        'warp:start',
  WARP_END:          'warp:end',
  MUSIC_UPDATE:      'music:update',
  PLAYER_FOOTSTEP:   'player:footstep',
  PLAYER_JUMP:       'player:jump',
  PLAYER_LAND:       'player:land',
  PLAYER_JETPACK_START: 'player:jetpack:start',
  PLAYER_JETPACK_STOP:  'player:jetpack:stop',
  PLAYER_BOOST:      'player:boost',
  ANOMALY_INTERACT:  'anomaly:interact',
  SHIP_THROTTLE:     'ship:throttle',
  SHIP_BOOST:        'ship:boost',
  MAP_OPEN:          'map:open',
  MAP_CLOSE:         'map:close',
  NOTIFICATION:      'ui:notification',

  // ── Narrative events (single-planet story) ──
  SPECIES_LISTENED:  'species:listened',     // { speciesId }
  SPECIES_ALL_HEARD: 'species:allHeard',     // all species cataloged
  TOOL_COLLECTED:    'tool:collected',        // { toolId, toolName }
  ALL_TOOLS:         'tool:allCollected',     // all repair tools found
  RADIO_REPAIRED:    'radio:repaired',        // radio fixed → endgame
  MUSIC_MAKER_SAVE:  'musicMaker:save',       // { songName, tracks }
  JOURNAL_OPEN:      'journal:open',
  JOURNAL_CLOSE:     'journal:close',
};
